import { React, moment } from 'Imports';
import { useState } from 'react';
import * as DateFormatter from '$Components/Shared/DateFormatter';
import { CSVLink } from 'react-csv';
import { Button, Dialog, DialogActions, DialogContent } from 'MaterialUIComponents';
import { ExportButton } from '$Components/FilterComponents/Buttons';
import { isEncompass } from '$Utilities/integrationPlatform';
import { IIntegrationPartnerDataInjectedProps, IntegrationPartnerDataService } from '$State/IntegrationPartnerDataFreezerService';
import { IDeviceAssociationServiceInjectedProps, DeviceAssociationService } from '$State/DeviceAssociationFreezerService';
import { canEditByRole, RolesEnum } from '$Externals/VerifyRole';
import { VehicleCameraPairingResponse } from '$Generated/api';

interface IDeviceAssociationExportBaseProps {
    csvTitle: string;
    filteredItems: VehicleCameraPairingResponse[];
    isMobileView?: boolean;
}
type IDeviceAssociationExportProps = IDeviceAssociationExportBaseProps &
    IDeviceAssociationServiceInjectedProps &
    IIntegrationPartnerDataInjectedProps;

const styles = require('./DeviceAssociationExport.scss') as {
    fileDownload: string;
    confirmationDialog: string;
    dialogAction: string;
    confirmMessage: string;
    confirmButton: string;
    cancelButton: string;
    dialog: string;
};

const _DeviceAssociationExport: React.FC<IDeviceAssociationExportProps> = (props) => {
    const { csvTitle, filteredItems, isMobileView } = props;

    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const canEdit = canEditByRole([RolesEnum.videoEventView, RolesEnum.videoEventEdit, RolesEnum.encompassVideo]);

    const showConfirmDialog = (show: boolean) => {
        if (!canEdit) return;
        setIsConfirmDialogOpen(show);
    };

    const csvHeaders: { key: string; label: string }[] = isEncompass
        ? [
              { label: 'Camera Serial', key: 'CameraSerial' },
              { label: 'Camera Device Type', key: 'CameraDeviceType' },
              { label: 'Paired Vehicle', key: 'PairedVehicle' },
              { label: 'GPS Tracker', key: 'isGpsTrackingEnabled' },
              { label: 'Date Paired', key: 'StartDate' },
              { label: 'Last Processed Event', key: 'LastProcessedEvent' },
              { label: 'Pairing Status', key: 'PairingStatus' },
              { label: 'Connection Status', key: 'ConnectionStatus' },
              { label: 'Last Connection Status Update', key: 'LastConnectionStatus' },
          ]
        : [
              { label: 'Camera Serial', key: 'CameraSerial' },
              { label: 'Camera Device Type', key: 'CameraDeviceType' },
              { label: 'GO Serial', key: 'GoSerial' },
              { label: 'Paired Vehicle', key: 'PairedVehicle' },
              { label: 'Date Paired', key: 'StartDate' },
              { label: 'Last Processed Event', key: 'LastProcessedEvent' },
              { label: 'Pairing Status', key: 'PairingStatus' },
              { label: 'Connection Status', key: 'ConnectionStatus' },
              { label: 'Last Connection Status Update', key: 'LastConnectionStatus' },
          ];

    const csvData = filteredItems.map((val) => {
        if (isEncompass) {
            return {
                CameraSerial: val.cameraDevice?.serialNumber ?? 'Unknown',
                CameraDeviceType: val.cameraDevice?.deviceType?.displayName ?? 'Unknown',
                PairedVehicle: val.vehicle?.name ?? 'Unknown',
                StartDate: val.startDate ? DateFormatter.date(moment(val.startDate) || moment()) : 'Unknown',
                LastProcessedEvent:
                    val.cameraDevice?.lastProcessedEventTime != null
                        ? DateFormatter.dateAndTimezone(moment(val.cameraDevice?.lastProcessedEventTime) || moment())
                        : '',
                PairingStatus: val.cameraDevice?.pairingStatus ?? '',
                ConnectionStatus: val.cameraDevice?.onlineStatus ?? 'Unknown',
                LastConnectionStatus:
                    val.cameraDevice?.lastHeartbeatTime != null
                        ? DateFormatter.dateAndTimezone(moment(val.cameraDevice?.lastHeartbeatTime) || moment())
                        : '',
                isGpsTrackingEnabled: val.isGpsTrackingEnabled ? 'On' : 'Off',
            };
        } else {
            return {
                CameraSerial: val.cameraDevice?.serialNumber ?? 'Unknown',
                CameraDeviceType: val.cameraDevice?.deviceType?.displayName ?? 'Unknown',
                GoSerial: val.vehicle?.telematicDevice?.serialNumber ?? 'Unknown',
                PairedVehicle: val.vehicle?.name ?? 'Unknown',
                StartDate: val.startDate ? DateFormatter.date(moment(val.startDate) || moment()) : 'Unknown',
                LastProcessedEvent:
                    val.cameraDevice?.lastProcessedEventTime != null
                        ? DateFormatter.dateAndTimezone(moment(val.cameraDevice?.lastProcessedEventTime) || moment())
                        : '',
                PairingStatus: val.cameraDevice?.pairingStatus ?? '',
                ConnectionStatus: val.cameraDevice?.onlineStatus ?? 'Unknown',
                LastConnectionStatus:
                    val.cameraDevice?.lastHeartbeatTime != null
                        ? DateFormatter.dateAndTimezone(moment(val.cameraDevice?.lastHeartbeatTime) || moment())
                        : '',
            };
        }
    });

    return (
        <span>
            <ExportButton onClick={() => showConfirmDialog(true)} disabled={!canEdit} />
            <Dialog
                open={isConfirmDialogOpen}
                className={`${styles.confirmationDialog} ${styles.dialog}`}
                PaperProps={{
                    sx: {
                        maxWidth: isMobileView ? '355px !important' : '600px',
                    },
                }}
            >
                <DialogContent className={styles.confirmMessage}>Are you sure you want to export these associations?</DialogContent>
                <DialogActions className={styles.dialogAction}>
                    <CSVLink data={csvData} headers={csvHeaders} filename={csvTitle + '.csv'} className={styles.fileDownload}>
                        <Button className={styles.confirmButton} onClick={() => showConfirmDialog(false)}>
                            Yes, Export
                        </Button>
                    </CSVLink>
                    <Button className={styles.cancelButton} onClick={() => showConfirmDialog(false)}>
                        No, Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </span>
    );
};

export const DeviceAssociationExport = IntegrationPartnerDataService.inject(DeviceAssociationService.inject(_DeviceAssociationExport));
